<template>
  <v-container
    fluid
    class="pa-6 d-flex flex-column align-center justify-center"
  >
    <!-- Main Stepper -->
    <v-stepper v-model="step" class="elevation-3 rounded-xl" max-width="1200">
      <!-- Step Headers -->
      <v-stepper-header>
        <v-stepper-step :complete="step > 1" step="1">Welcome</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="step > 2" step="2"
          >Create Organization</v-stepper-step
        >

        <v-divider></v-divider>
        <v-stepper-step :complete="step > 3" step="3"
          >First Location</v-stepper-step
        >
        <v-divider></v-divider>
        <v-stepper-step :complete="step > 4" step="4"
          >Plan & Payment</v-stepper-step
        >
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content step="1">
          <v-card flat class="text-center py-10 px-6">
            <v-icon color="primary" size="80">mdi-hand-wave</v-icon>
            <h1 class="mt-4 font-weight-bold">Welcome to {{ clientName }}</h1>
            <p class="subtitle-1 grey--text mt-2">
              Let’s set up your organization, create your first location, and
              select your plan.
            </p>
            <v-btn color="primary" rounded large class="mt-6" @click="step = 2">
              Get Started
            </v-btn>
          </v-card>
        </v-stepper-content>

        <v-stepper-content step="2">
          <v-card flat class="pa-6">
            <h2 class="mb-4">Create Organization</h2>
            <p class="subtitle-2 grey--text mb-6">
              Enter your organization information. All your business data will
              be saved under this account.
            </p>

            <v-text-field
              ref="organizationName"
              v-model="Organization.name"
              label="Organization Name *"
              outlined
              dense
              autofocus
              color="primary"
              :error-messages="organizationNameErrors"
              @input="clearAvailabilityConflict"
              :disabled="availabilityChecking"
            />
            <v-text-field
              v-model="Organization.phoneNumber"
              label="Phone Number *"
              outlined
              dense
              color="primary"
              :error-messages="organizationPhoneNumberErrors"
              :disabled="availabilityChecking"
            />
            <v-text-field
              ref="organizationEmail"
              v-model="Organization.email"
              label="Email *"
              outlined
              dense
              color="primary"
              :error-messages="organizationEmailErrors"
              @input="clearAvailabilityConflict"
              :disabled="availabilityChecking"
            />
            <v-alert v-if="availabilityError" type="error" outlined dense role="alert">
              {{ availabilityError }}
            </v-alert>

            <v-row class="mt-6">
              <v-btn outlined rounded color="grey" @click="step = 1" :disabled="availabilityChecking"
                >Back</v-btn
              >
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                rounded
                @click="nextStageAndPrefill"
                :loading="availabilityChecking"
                :disabled="$v.Organization.$invalid || availabilityChecking"
              >
                Continue
              </v-btn>
            </v-row>
          </v-card>
        </v-stepper-content>

        <v-stepper-content step="3">
          <v-card flat class="pa-6">
            <h2 class="mb-4">Create Your First Location</h2>
            <p class="subtitle-2 grey--text mb-6">
              Start with your main store. This information will appear on
              receipts and invoices.
            </p>

            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="Location.name"
                  label="Business Name *"
                  outlined
                  dense
                  autofocus
                  color="primary"
                  :error-messages="locationNameErrors"
                />
              </v-col>

              <!-- <v-col cols="12" sm="6">
                <v-select
                  v-model="Location.businessType"
                  :items="businessTypes"
                  label="Business Type *"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  color="primary"
                  :error-messages="businessTypeErrors"
                />
              </v-col> -->
            </v-row>

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="Location.email"
                  label="Email *"
                  outlined
                  dense
                  color="primary"
                  :error-messages="locationEmailErrors"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="Location.website"
                  label="Website (optional)"
                  outlined
                  dense
                  color="primary"
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="Location.Address.phoneNumber"
                  label="Phone Number *"
                  outlined
                  dense
                  color="primary"
                  :error-messages="phoneErrors"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="Location.Address.addressLine1"
                  label="Address Line 1 *"
                  outlined
                  dense
                  color="primary"
                  :error-messages="addressLine1Errors"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="Location.Address.addressLine2"
                  label="Address Line 2"
                  outlined
                  dense
                  color="primary"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="Location.Address.city"
                  label="City *"
                  outlined
                  dense
                  color="primary"
                  :error-messages="cityErrors"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="Location.Address.StateId"
                  :items="states"
                  item-text="name"
                  item-value="id"
                  label="State *"
                  outlined
                  dense
                  color="primary"
                  :error-messages="stateIdErrors"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="Location.Address.postalCode"
                  label="Zip Code *"
                  outlined
                  dense
                  color="primary"
                  :error-messages="postalCodeErrors"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="Location.Address.CountryCode"
                  :items="countries"
                  item-text="name"
                  item-value="code"
                  label="Country *"
                  outlined
                  dense
                  color="primary"
                  :error-messages="countryCodeErrors"
                  @change="countrySelectionTouched = true"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="Location.timeZone"
                  :items="timeZones"
                  item-text="label"
                  item-value="tzCode"
                  label="Time Zone *"
                  outlined
                  dense
                  color="primary"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row class="mt-6">
              <v-btn outlined rounded color="grey" @click="step = 2"
                >Back</v-btn
              >
              <v-spacer />
              <v-btn
                color="primary"
                rounded
                @click="step = 4"
                :disabled="$v.Location.$invalid"
              >
                Continue
              </v-btn>
            </v-row>
          </v-card>
        </v-stepper-content>

        <v-stepper-content step="4">
          <v-card flat class="pa-6">
            <h2 class="mb-4">Choose a Plan</h2>
            <v-container grid-list-lg>
              <p class="subtitle-2 grey--text mb-6">
                Pick a plan that fits your business needs. You can upgrade or
                cancel anytime.
              </p>

              <v-progress-linear v-if="plansLoading" indeterminate color="primary" class="mb-4" aria-label="Loading plans" />
              <v-row aria-label="Available plans">
                <v-col
                  v-for="plan in plans"
                  :key="plan.id"
                  cols="12"
                  sm="6"
                  md="4"
                  class="d-flex"
                >
                  <v-card
                    outlined
                    class="flex-grow-1 text-center plan-card"
                    :class="{
                      active: selectedPlan && selectedPlan.id === plan.id,
                    }"
                    @click="selectPlan(plan)"
                    @keydown.enter.prevent="selectPlan(plan)"
                    @keydown.space.prevent="selectPlan(plan)"
                    role="button"
                    tabindex="0"
                    :aria-pressed="selectedPlan && selectedPlan.id === plan.id ? 'true' : 'false'"
                  >
                    <v-card-text>
                      <v-chip v-if="selectedPlan && selectedPlan.id === plan.id" small color="primary" outlined class="mb-2">Selected</v-chip>
                      <h3 class="font-weight-bold mb-2">{{ plan.name }}</h3>
                      <div class="display-1 font-weight-bold mb-2">
                        {{ planPriceLabel(plan) }}
                      </div>
                      <p class="caption grey--text text--darken-1">
                        {{ plan.description }}
                      </p>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
              <v-alert v-if="!plansLoading && plans.length && !selectedPlan" type="info" outlined dense class="mt-4">
                Select one plan to continue.
              </v-alert>
              <v-alert v-if="!plansLoading && !plans.length && !planError" type="info" outlined dense class="mt-4">
                No signup plans are currently available for this brand. Please contact support before continuing.
              </v-alert>
              <v-alert v-if="planError" type="error" outlined dense role="alert" class="mt-4">
                {{ planError }} <v-btn text small color="error" @click="fetchPlans">Retry plans</v-btn>
              </v-alert>

              <div
                v-if="paidPlan"
                class="text-center mt-8"
              >
                <h3 class="mb-3">Payment Method</h3>
                <v-sheet class="pa-4 rounded-lg elevation-1" outlined>
                  <v-alert v-if="stripeConfigError" type="error" outlined dense>
                    {{ stripeConfigError }}
                    <v-btn text small color="error" @click="loadSignupStripeConfig">Retry payment setup</v-btn>
                  </v-alert>
                  <card
                    v-if="stripePublicKey"
                    class="stripe-card my-4"
                    :class="{ complete }"
                    :stripe="stripePublicKey"
                    @change="complete = $event.complete"
                  />
                  <v-img
                    src="@/assets/images/powered_by_stripe.svg"
                    height="26"
                    contain
                    class="mx-auto"
                  />
                </v-sheet>
              </div>

              <div v-if="paidPlan" class="text-center mb-6 mt-5">
                <p class="font-weight-medium text--secondary">
                  By entering your card details and clicking
                  <strong>"Confirm & Create Organization"</strong>, you
                  authorize
                  <span class="font-weight-bold">{{ clientName }}</span> to
                  automatically charge your selected plan’s amount on its
                  displayed billing schedule until cancelled.
                </p>
              </div>

              <!-- Action Buttons -->
              <v-row class="mt-4">
                <v-btn outlined rounded color="grey" @click="step = 3">
                  Back
                </v-btn>
                <v-spacer />
                <v-btn
                  rounded
                  color="red darken-1"
                  dark
                  @click="submit"
                  :loading="isLoading"
                  :disabled="!selectedPlan || plansLoading || !!planError || (paidPlan && (!stripePublicKey || !complete))"
                >
                  Confirm & Create Organization
                </v-btn>
              </v-row>
            </v-container>
          </v-card>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-container>
</template>

<script>
import { Card, createToken } from "vue-stripe-elements-plus";
import { mapGetters, mapActions } from "vuex";
import timezones from "compact-timezone-list";
import publicService from "@/api/public";
import organizationService from "../Organization/service.js";
import stripeConfigService from "@/api/stripeConfig";
import { unitedStatesCountryCode } from "@/config/defaultCountry";

const { validationMixin } = require("vuelidate");
const { required, minLength, email } = require("vuelidate/lib/validators");

export default {
  name: "new-organization-stepper",
  components: { Card },
  mixins: [validationMixin],

  data() {
    return {
      step: 1,
      complete: false,

      stripePublicKey: "",
      stripeConfigError: "",
      isLoading: false,

      clientName: "",
      appSource: "",

      timeZones: timezones,

      /* Organization form */
      Organization: {
        name: null,
        phoneNumber: null,
        email: null,
        cardToken: null,
      },

      /* Location form */
      Location: {
        name: null,
        businessType: null, // auto-filled by brand
        website: null,
        email: null,
        appSource: "",
        timeZone: this.$moment.tz.guess() || "America/New_York",
        Address: {
          addressLine1: null,
          addressLine2: null,
          city: null,
          StateId: null,
          CountryCode: null,
          postalCode: null,
          phoneNumber: null,
        },
      },

      plans: [],
      plansLoading: false,
      planError: "",
      selectedPlan: null,
      availabilityChecking: false,
      availabilityError: "",
      availabilityConflictFields: [],
      lastLocationPrefill: { name: "", email: "", phoneNumber: "" },
      countrySelectionTouched: false,
    };
  },

  async created() {
    this.applyBrandSettings();
    this.Location.appSource = this.appSource;
    await this.loadSignupStripeConfig();
    this.fetchPlans();
  },

  computed: {
    ...mapGetters("global", ["states", "countries"]),
    ...mapGetters("brand", ["currentBrand"]),
    paidPlan() {
      return !!(this.selectedPlan && this.selectedPlan.requiresCard !== false && parseFloat(this.selectedPlan.amount) > 0);
    },

    /* All validation errors stay unchanged */
    phoneErrors() {
      const e = [];
      if (!this.$v.Location.Address.phoneNumber.$dirty) return e;
      if (!this.$v.Location.Address.phoneNumber.required)
        e.push("Phone number is required.");
      return e;
    },
    organizationNameErrors() {
      const e = [];
      if (!this.$v.Organization.name.$dirty) return e;
      if (!this.$v.Organization.name.required)
        e.push("This field is required.");
      if (!this.$v.Organization.name.minLength)
        e.push("Must be at least 4 characters long.");
      if (this.availabilityConflictFields.includes("name"))
        e.push("This organization name and email are already in use together.");
      return e;
    },
    organizationPhoneNumberErrors() {
      const e = [];
      if (!this.$v.Organization.phoneNumber.$dirty) return e;
      if (!this.$v.Organization.phoneNumber.required)
        e.push("Phone number is required.");
      if (!this.$v.Organization.phoneNumber.minLength)
        e.push("Invalid phone number.");
      return e;
    },
    organizationEmailErrors() {
      const e = [];
      if (!this.$v.Organization.email.$dirty) return e;
      if (!this.$v.Organization.email.required)
        e.push("This field is required.");
      if (!this.$v.Organization.email.email) e.push("Email is not valid.");
      if (this.availabilityConflictFields.includes("email"))
        e.push("This organization name and email are already in use together.");
      return e;
    },
    locationNameErrors() {
      const e = [];
      if (!this.$v.Location.name.$dirty) return e;
      if (!this.$v.Location.name.required) e.push("This field is required.");
      if (!this.$v.Location.name.minLength)
        e.push("Must be at least 4 characters long.");
      return e;
    },
    locationEmailErrors() {
      const e = [];
      if (!this.$v.Location.email.$dirty) return e;
      if (!this.$v.Location.email.email) e.push("Email is not valid.");
      return e;
    },
    addressLine1Errors() {
      const e = [];
      if (!this.$v.Location.Address.addressLine1.$dirty) return e;
      if (!this.$v.Location.Address.addressLine1.required)
        e.push("Address is required.");
      return e;
    },
    cityErrors() {
      const e = [];
      if (!this.$v.Location.Address.city.$dirty) return e;
      if (!this.$v.Location.Address.city.required) e.push("City is required.");
      return e;
    },
    postalCodeErrors() {
      const e = [];
      if (!this.$v.Location.Address.postalCode.$dirty) return e;
      if (!this.$v.Location.Address.postalCode.required)
        e.push("Zip Code is required.");
      return e;
    },
    stateIdErrors() {
      const e = [];
      if (!this.$v.Location.Address.StateId.$dirty) return e;
      if (!this.$v.Location.Address.StateId.required)
        e.push("State is required.");
      return e;
    },
    countryCodeErrors() {
      const e = [];
      if (!this.$v.Location.Address.CountryCode.$dirty) return e;
      if (!this.$v.Location.Address.CountryCode.required)
        e.push("Country is required.");
      return e;
    },
  },

  watch: {
    countries: {
      immediate: true,
      handler() { this.applyDefaultCountry(); },
    },
  },

  methods: {
    applyDefaultCountry() {
      if (this.countrySelectionTouched || this.Location.Address.CountryCode != null) return;
      const code = unitedStatesCountryCode(this.countries);
      if (code != null) this.Location.Address.CountryCode = code;
    },
    ...mapActions("global", ["initOrganization"]),
    planPriceLabel(plan) {
      if (!plan) return "";
      const amount = new Intl.NumberFormat(undefined, { style: "currency", currency: String(plan.currency || "USD").toUpperCase() }).format(Number(plan.amount));
      const count = Number(plan.intervalCount) || 1;
      const interval = plan.interval || "billing period";
      return `${amount} every ${count > 1 ? `${count} ` : ""}${interval}${count > 1 ? "s" : ""}`;
    },
    async loadSignupStripeConfig() {
      this.stripePublicKey = "";
      this.stripeConfigError = "";
      try { this.stripePublicKey = (await stripeConfigService.getSignupConfig(this.currentBrand)).publishableKey; }
      catch (error) { this.stripeConfigError = "Payment setup is unavailable for this brand. Try again or contact support."; }
    },

    /* 🔥 Brand → Auto-fill label + business type + appSource */
    applyBrandSettings() {
      const brand = this.currentBrand;

      if (brand === "storesync") {
        this.clientName = "StoreSync POS";
        this.appSource = "storesyncpos";
        this.Location.businessType = "wireless"; // your default, change if needed
      } else if (brand === "autorepair") {
        this.clientName = "AutoRepair Desk";
        this.appSource = "autorepairdesk";
        this.Location.businessType = "auto";
      } else if (brand === "repairnest") {
        this.clientName = "RepairNest POS";
        this.appSource = "repairnestpos";
        this.Location.businessType = "repair";
      } else {
        // fallback
        this.clientName = "StoreSync POS";
        this.appSource = "storesyncpos";
        this.Location.businessType = "wireless";
      }
    },

    async fetchPlans() {
      this.plansLoading = true;
      this.planError = "";
      try {
        const res = await publicService.GetPlans({
          audience: "LOCATION",
          includeIntegrations: true,
        }, this.currentBrand);
        this.plans = res.data || [];
      } catch (e) {
        this.plans = [];
        this.planError = "Plans could not be loaded. Try again before creating your organization.";
      } finally {
        this.plansLoading = false;
      }
    },

    /* Continue to Location step */
    async nextStageAndPrefill() {
      if (this.availabilityChecking) return;
      this.$v.Organization.$touch();
      if (this.$v.Organization.$invalid) return;
      this.availabilityChecking = true;
      this.clearAvailabilityConflict();
      const checkedIdentity = {
        name: String(this.Organization.name || "").trim(),
        email: String(this.Organization.email || "").trim().toLowerCase(),
      };
      try {
        await organizationService.checkAvailability(checkedIdentity, this.currentBrand);
      } catch (error) {
        if (this.isAuthenticationFailure(error)) {
          await this.showExpiredSetupSession();
          return;
        }
        if (this.isOrganizationConflict(error)) {
          this.showOrganizationConflict(error);
          return;
        }
        this.availabilityError = "We couldn’t verify organization availability. Please try again.";
        return;
      } finally {
        this.availabilityChecking = false;
      }
      const currentIdentity = {
        name: String(this.Organization.name || "").trim(),
        email: String(this.Organization.email || "").trim().toLowerCase(),
      };
      if (this.step !== 2 || currentIdentity.name !== checkedIdentity.name || currentIdentity.email !== checkedIdentity.email) return;
      this.prefillLocationField("name", this.Organization.name);
      this.prefillLocationField("email", this.Organization.email);
      const currentPhone = this.Location.Address.phoneNumber;
      if (!currentPhone || currentPhone === this.lastLocationPrefill.phoneNumber) {
        this.Location.Address.phoneNumber = this.Organization.phoneNumber;
        this.lastLocationPrefill.phoneNumber = this.Organization.phoneNumber;
      }
      this.step = 3;
    },
    prefillLocationField(field, value) {
      if (!this.Location[field] || this.Location[field] === this.lastLocationPrefill[field]) {
        this.Location[field] = value;
        this.lastLocationPrefill[field] = value;
      }
    },
    clearAvailabilityConflict() {
      this.availabilityError = "";
      this.availabilityConflictFields = [];
    },
    conflictBody(error) {
      return (error && (error.data || (error.response && error.response.data))) || {};
    },
    isOrganizationConflict(error) {
      const body = this.conflictBody(error);
      return Number(error && (error.status || (error.response && error.response.status))) === 409 || body.code === "ORGANIZATION_IDENTITY_CONFLICT" || body.publicCode === "ORGANIZATION_IDENTITY_CONFLICT";
    },
    isAuthenticationFailure(error) {
      const body = this.conflictBody(error);
      return Number(error && (error.status || (error.response && error.response.status) || body.httpStatus)) === 401;
    },
    async showExpiredSetupSession() {
      await this.$swal.fire({
        icon: "info",
        title: "Sign in to restart setup",
        text: "Your setup session has expired. For your security, sign in again. You’ll need to restart organization setup.",
        confirmButtonText: "Go to sign in",
      });
      this.$router.replace({
        name: "authentication-login",
      });
    },
    showOrganizationConflict(error) {
      const body = this.conflictBody(error);
      const details = body.data || body.metadata || {};
      this.step = 2;
      this.availabilityConflictFields = (details.conflict && details.conflict.fields) || details.fields || ["name", "email"];
      this.availabilityError = body.message || "An organization with this name and email already exists.";
      this.$nextTick(() => {
        const field = this.availabilityConflictFields.includes("name") ? this.$refs.organizationName : this.$refs.organizationEmail;
        if (field && field.focus) field.focus();
      });
    },

    selectPlan(plan) {
      this.planError = "";
      this.selectedPlan = plan;
    },

    selectedBillingPlanId() {
      return this.selectedPlan && (this.selectedPlan.planId || this.selectedPlan.code);
    },

    async submit() {
      if (this.isLoading) return;
      if (!this.selectedPlan) {
        this.planError = "Choose a plan before creating your organization.";
        return;
      }
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$toasted.error("Please fill all required fields.", {
          position: "top-right",
        });
        return;
      }

      this.isLoading = true;

      try {
        if (this.paidPlan && (!this.stripePublicKey || !this.complete)) throw new Error(this.stripeConfigError || "Complete card setup before creating this paid organization.");
        const { token } = this.complete ? await createToken() : {};
        if (token) this.Organization.cardToken = token.id;

        const billingPlanId = this.selectedBillingPlanId();
        if (!billingPlanId) throw new Error("The selected plan is missing its billing identifier. Refresh the plans and choose it again.");
        this.Location.planId = billingPlanId;

        const res = await organizationService.create({
          Organization: this.Organization,
          Location: this.Location,
        }, this.currentBrand);

        await this.initOrganization(res.data);
        this.$router.push({ path: "/" });
      } catch (err) {
        if (this.isAuthenticationFailure(err)) {
          await this.showExpiredSetupSession();
          return;
        }
        if (this.isOrganizationConflict(err)) {
          this.showOrganizationConflict(err);
          return;
        }
        this.$toasted.error(
          err?.data?.message || err.message || "Failed to create organization.",
          { position: "top-right" }
        );
      } finally {
        this.isLoading = false;
      }
    },
  },

  /* Validation (business type removed) */
  validations: {
    Organization: {
      name: { required, minLength: minLength(4) },
      phoneNumber: { required, minLength: minLength(10) },
      email: { required, email },
    },
    Location: {
      name: { required, minLength: minLength(4) },
      email: { email },
      Address: {
        phoneNumber: { required, minLength: minLength(10) },
        addressLine1: { required },
        city: { required },
        postalCode: { required },
        StateId: { required },
        CountryCode: { required },
      },
    },
  },
};
</script>

<style scoped>
.business-tile {
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.25s ease;
  background: #fff;
  min-height: 240px;
}
.business-tile:hover {
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
  transform: translateY(-3px);
}
.business-tile.active {
  border: 2px solid #1976d2;
  background: linear-gradient(145deg, #e3f2fd, #ffffff);
  box-shadow: 0 0 10px rgba(25, 118, 210, 0.3);
}
.plan-card {
  border: 1px solid rgba(127, 127, 127, 0.35);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.plan-card:hover {
  border-color: var(--v-primary-base);
}
.plan-card.active {
  border: 2px solid var(--v-primary-base);
}
.plan-card:focus-visible {
  outline: 3px solid var(--v-primary-base);
  outline-offset: 2px;
}
.stripe-card {
  border: 1px solid rgba(127, 127, 127, 0.5);
  border-radius: 8px;
  padding: 14px;
}
</style>
